import React, { forwardRef } from "react";

export const InputField = forwardRef(
  (
    {
      label,
      name,
      type = "text",
      value,
      onChange,
      required = false,
      placeholder,
      disabled,
      options,
      defaultOptionLabel = "Pilih...",
      error,
      labelColor = "text-white",
      accept,
      rows = 4,
      style,
      startIcon,
      labelClassName = "", 
      className = "",
      variant = "standard", 
      wrapperClassName = "",
      children,
      ...props
    },
    ref
  ) => {
    const hideLabel = labelClassName.includes("sr-only");

    const getFieldClassName = () => {
      let base = `input w-full px-4 py-2.5 h-auto text-sm border rounded-xl bg-white text-black disabled:bg-gray-100 disabled:text-gray-500 ${error ? "border-red-500" : "border-slate-200"}`;
      
      if (type === "file") {
        base = `${base} file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200`;
      }
      
      if (type === "textarea") {
        base = `${base} resize-y min-h-[120px]`;
      }
      
      if (startIcon) {
        base = `${base} pl-10`;
      }
      
      return `${base} ${className}`;
    };

    return (
      <div
        className={`${
          // Full width unless the caller sets one: with both classes present
          // Tailwind's order decides, and w-full always won over w-32 / w-72.
          /(^|\s)(w-|flex-1)/.test(wrapperClassName) ? "" : "w-full"
        } ${hideLabel ? "flex items-center" : "flex flex-col"} ${wrapperClassName}`}
      >
        {label && (
          <label
            htmlFor={name}
            className={`${labelColor} mb-2 ${labelClassName} ${
              hideLabel ? "mb-0" : ""
            }`}
          >
            {label} {required && "*"}
          </label>
        )}

        {type === "select" ? (
          <select
            name={name}
            id={name}
            value={value}
            onChange={onChange}
            required={required}
            disabled={disabled}
            ref={ref}
            aria-describedby={error ? `${name}-error` : undefined}
            className={getFieldClassName()}
           {...props}>
            {/* Only a required select locks its placeholder: there the blank
                value is a prompt, not an answer. On an optional select the blank
                IS a valid answer, so disabling it would trap anyone who picked a
                value and then wanted to clear it. */}
            {defaultOptionLabel !== null && defaultOptionLabel !== undefined && (
              <option value="" disabled={required}>
                {defaultOptionLabel}
              </option>
            )}
            {children}
            {options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : type === "file" ? (
          <input
            type="file"
            name={name}
            id={name}
            onChange={onChange}
            required={required}
            disabled={disabled}
            accept={accept}
            ref={ref}
            aria-describedby={error ? `${name}-error` : undefined}
            className={getFieldClassName()}
          {...props} />
        ) : type === "textarea" ? (
          <textarea
            name={name}
            id={name}
            value={value}
            onChange={onChange}
            required={required}
            placeholder={placeholder}
            disabled={disabled}
            rows={rows}
            style={style}
            ref={ref}
            aria-describedby={error ? `${name}-error` : undefined}
            className={getFieldClassName()}
          {...props} />
        ) : (
          <div className="relative w-full">
            {startIcon && (
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                {startIcon}
              </span>
            )}
            <input
              type={type}
              name={name}
              id={name}
              value={value}
              onChange={onChange}
              required={required}
              placeholder={placeholder}
              disabled={disabled}
              ref={ref}
              aria-describedby={error ? `${name}-error` : undefined}
              className={getFieldClassName()}
            {...props} />
          </div>
        )}

        {error && (
          <p id={`${name}-error`} className="text-red-500 text-sm mt-1">
            {error}
          </p>
        )}
      </div>
    );
  }
);

InputField.displayName = "InputField";
